import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';

interface FailClosedNoticeProps {
  /**
   * Optional label describing which field collapsed (e.g. "Summary of findings").
   * Used to make the warning self-describing when the notice is rendered inline
   * without surrounding section context.
   */
  fieldLabel?: string;
  className?: string;
  /** Density variant. "inline" is used for narrow per-row contexts (e.g. table cells). */
  variant?: 'block' | 'inline';
}

/**
 * Distinct UI state shown when the audit pipeline replaced a narrative field
 * with the canonical fail-closed placeholder. The user must see a warning
 * banner and not the literal placeholder string in body copy.
 *
 * Uses the same amber warning treatment the rest of the report uses for
 * partial / unsafe states (see Screening Errors and "Please note" blockquotes).
 */
export function FailClosedNotice({
  fieldLabel,
  className,
  variant = 'block',
}: FailClosedNoticeProps) {
  if (variant === 'inline') {
    return (
      <div
        role="alert"
        data-testid="fail-closed-notice"
        className={cn(
          'flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900',
          className,
        )}
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <span>
          {fieldLabel ? `${fieldLabel} unavailable. ` : ''}
          Manual review required — automated rationale could not be safely produced for this item.
        </span>
      </div>
    );
  }

  return (
    <div
      role="alert"
      data-testid="fail-closed-notice"
      className={cn(
        'rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <p className="font-semibold text-amber-900">
            {fieldLabel ? `${fieldLabel} unavailable` : 'Rationale unavailable'}
          </p>
          <p className="mt-1 text-amber-800">
            The automated rationale could not be safely produced for this section. Manual review is
            required before relying on this finding.
          </p>
        </div>
      </div>
    </div>
  );
}
